import { createFileRoute } from '@tanstack/react-router';
import { Suspense } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, CardHeader } from '~/components/ui';
import { JobsTable } from '~/components/dashboard/JobsTable';
import { TabErrorBoundary } from '~/components/TabErrorBoundary';
import { DASHBOARDS, getTab, type TabDef } from '~/lib/dashboards';
import { useAuth } from '~/lib/auth';
import { canSeeTab } from '~/lib/access';
import { useRouteAnalytics } from '~/lib/analytics';

/**
 * Catch-all dashboard tab dispatcher. Static lookup first, then falls
 * back to the dashboard's dynamicTabs() (per-salesperson, per-source).
 * Specific route files (e.g. _app.dashboards.operations.dispatch.tsx)
 * win over this one — file-based routing prefers more-specific matches.
 */
export const Route = createFileRoute('/_app/dashboards/$dashboard/$tab')({
  component: TabDispatcher,
});

function TabDispatcher() {
  const { dashboard: dashKey, tab: tabKey } = Route.useParams();
  const dashboard = DASHBOARDS[dashKey];
  const { profile, hasTabAccess } = useAuth();

  // Fire a `dashboard_page_views` row each time (dashboard, tab) changes.
  // Feeds the Company Overview → Dashboard Utilization tab.
  useRouteAnalytics(dashKey, tabKey);

  const { data: dynamicTabs } = useQuery({
    queryKey: ['dynamic_tabs', dashKey],
    queryFn: async () => dashboard?.dynamicTabs ? await dashboard.dynamicTabs() : [],
    enabled: !!dashboard?.dynamicTabs,
    staleTime: 5 * 60 * 1000,
  });

  if (!dashboard) {
    return (
      <Card>
        <CardHeader>Unknown dashboard</CardHeader>
        <CardBody><div className="text-sm">No dashboard "{dashKey}".</div></CardBody>
      </Card>
    );
  }

  let tab: TabDef | undefined = getTab(dashKey, tabKey)?.tab;
  if (!tab) tab = (dynamicTabs ?? []).find((t) => t.key === tabKey);

  if (!tab) {
    return (
      <Card>
        <CardHeader>Unknown tab</CardHeader>
        <CardBody>
          <div className="text-sm text-[var(--color-ink-muted)]">
            No tab "{tabKey}" in this dashboard. Pick one from the strip above.
          </div>
        </CardBody>
      </Card>
    );
  }

  // Tab-level access guard for direct navigation (ACCESS-CONTROL-PORT.md §4/§5).
  const roles = (profile?.staff_role_keys as string[] | null) ?? [];
  const allowed = profile?.allowed_tabs as Record<string, string[]> | null;
  if (!canSeeTab({
    dashboardKey: dashKey, tabKey: tab.key, tabLabel: tab.label,
    roles, allowed, tabAllowed: hasTabAccess,
  })) {
    return (
      <Card>
        <CardHeader>Access restricted</CardHeader>
        <CardBody>
          <div className="text-sm text-[var(--color-ink-muted)]">
            You don't have access to "{tab.label}". Contact an admin if you need it.
          </div>
        </CardBody>
      </Card>
    );
  }

  if (tab.kind === 'jobs-table' && tab.jobs) {
    return (
      <div className="space-y-4">
        {tab.jobs.hint && <div className="text-sm text-[var(--color-ink-soft)]">{tab.jobs.hint}</div>}
        <JobsTable title={tab.label} spec={tab.jobs} />
      </div>
    );
  }

  if (tab.kind === 'custom' && tab.component) {
    const Comp = tab.component;
    return (
      <TabErrorBoundary label={tab.label}>
        <Suspense fallback={<div className="text-sm text-[var(--color-ink-muted)]">Loading…</div>}>
          <Comp />
        </Suspense>
      </TabErrorBoundary>
    );
  }

  if (tab.kind === 'custom' && tab.componentWithSlug) {
    const Comp = tab.componentWithSlug;
    return (
      <TabErrorBoundary label={tab.label}>
        <Suspense fallback={<div className="text-sm text-[var(--color-ink-muted)]">Loading…</div>}>
          <Comp slug={tab.key} />
        </Suspense>
      </TabErrorBoundary>
    );
  }

  if (tab.kind === 'route') {
    return (
      <Card>
        <CardHeader>{tab.label}</CardHeader>
        <CardBody>
          <div className="text-sm text-[var(--color-ink-muted)]">
            This tab is handled by its own route file. If you're seeing this, the file may be missing — expected at <code className="font-mono">_app.dashboards.{dashKey}.{tabKey}.tsx</code>.
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>{tab.label}</CardHeader>
      <CardBody>
        <div className="text-sm text-[var(--color-ink-soft)]">
          {tab.stubNote ?? 'Coming in a later Phase 3 wave.'}
        </div>
      </CardBody>
    </Card>
  );
}
